import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Lightweight hook that checks and toggles the favorite status of a single arche.
 * Used by FavoriteButton in arche headers and marketplace.
 */
export function useFavoriteStatus(archId: string) {
  const queryClient = useQueryClient();
  const statusQueryKey = queryKeys.favorites.status(archId);

  const { data, isLoading } = useQuery({
    queryKey: statusQueryKey,
    queryFn: () => api.checkFavorite(archId),
    enabled: !!archId,
  });

  const toggleMut = useMutation({
    mutationFn: async (currentlyFavorite: boolean) => {
      if (currentlyFavorite) {
        await api.removeFavorite(archId);
      } else {
        await api.addFavorite(archId);
      }
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: statusQueryKey });
      const previous = queryClient.getQueryData<{ isFavorite: boolean }>(statusQueryKey);
      queryClient.setQueryData(statusQueryKey, {
        isFavorite: !previous?.isFavorite,
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(statusQueryKey, context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: statusQueryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.favorites.list() });
    },
  });

  const isFavorite = data?.isFavorite ?? false;

  return {
    isFavorite,
    isLoading,
    toggle: async () => {
      await toggleMut.mutateAsync(isFavorite);
    },
  };
}
